import { useEffect, useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowCircleDown, Call, SearchNormal, TagUser } from "iconsax-reactjs";
import {
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Shield,
  UserMinus,
  X,
} from "lucide-react";
import {
  Flyin,
  FlyinBody,
  FlyinHeader,
  PaginationControls,
  FlyinTitle,
} from "../../../../shared/ui";
import { Avatar } from "../../../../shared/ui/DataTable";
import { formatPermissionLabel } from "../../../../shared/lib";
import { Member } from "./types";

type MemberDetailsFlyinProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeMember: Member | null;
  onAssignAccountManager: () => void;
  onAssignRole: () => void;
  onAddPermissions: () => void;
  onResendInvite: () => void;
  onRemoveMember: () => void;
  onChangeCustomerAccountManager?: (businessId: string) => void;
};

const MemberDetailsFlyin = ({
  open,
  onOpenChange,
  activeMember,
  onAssignAccountManager,
  onAssignRole,
  onAddPermissions,
  onResendInvite,
  onRemoveMember,
  onChangeCustomerAccountManager = () => undefined,
}: MemberDetailsFlyinProps) => {
  const [showAllPermissions, setShowAllPermissions] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customersPage, setCustomersPage] = useState(1);
  const customersPerPage = 5;

  useEffect(() => {
    if (!open) {
      setShowAllPermissions(false);
      setCustomerQuery("");
      setCustomersPage(1);
    }
  }, [open]);

  const visiblePermissions = useMemo(() => {
    const permissions = activeMember?.permissions ?? [];
    return showAllPermissions ? permissions : permissions.slice(0, 1);
  }, [activeMember?.permissions, showAllPermissions]);

  const totalPermissions = activeMember?.permissions.length ?? 0;
  const hiddenPermissionsCount = Math.max(totalPermissions - 1, 0);

  const filteredCustomers = useMemo(() => {
    const customers = activeMember?.businesses ?? [];
    const normalized = customerQuery.trim().toLowerCase();
    if (!normalized) return customers;

    return customers.filter((business) =>
      business.name.toLowerCase().includes(normalized),
    );
  }, [activeMember?.businesses, customerQuery]);
  const totalCustomerPages = Math.max(
    1,
    Math.ceil(filteredCustomers.length / customersPerPage),
  );
  const pagedCustomers = useMemo(() => {
    const start = (customersPage - 1) * customersPerPage;
    return filteredCustomers.slice(start, start + customersPerPage);
  }, [customersPage, filteredCustomers]);

  useEffect(() => {
    setCustomersPage(1);
  }, [customerQuery, activeMember?.id]);

  useEffect(() => {
    setCustomersPage((prev) => Math.min(prev, totalCustomerPages));
  }, [totalCustomerPages]);

  return (
    <Flyin open={open} onOpenChange={onOpenChange}>
      <FlyinHeader>
        <div className="flex items-center gap-4">
          <Avatar name={activeMember?.name ?? ""} email={activeMember?.email} />
          <div>
            <FlyinTitle>{activeMember?.name ?? "Member"}</FlyinTitle>
            <p className="mt-1 text-[14px] font-[400] text-[#6B6F8A]">
              {activeMember?.email ?? ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close panel"
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={() => onOpenChange(false)}
        >
          <X size={20} />
        </button>
      </FlyinHeader>

      <FlyinBody className="space-y-6">
        <div className="border-y border-[#E6E9F5] py-4">
          <div className="flex items-center gap-8 text-[16px] font-[500] text-[#161827]">
            {!activeMember?.showStatusDot ? (
              <button
                type="button"
                className="inline-flex items-center gap-2"
                onClick={onAssignAccountManager}
              >
                <TagUser size={20} className="text-[#6E7397]" />
                Assign as account manager
              </button>
            ) : null}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-2"
                >
                  More <ArrowCircleDown size={18} className="text-[#6E7397]" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={10}
                  className="z-[120] min-w-[320px] rounded-[20px] border border-[#E1E6FF] bg-white p-2 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
                >
                  {activeMember?.showStatusDot ? (
                    <DropdownMenu.Item
                      onSelect={onResendInvite}
                      className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                    >
                      <RefreshCw size={20} className="text-[#6E7397]" />
                      Resend invite
                    </DropdownMenu.Item>
                  ) : (
                    <>
                      <DropdownMenu.Item
                        onSelect={onAssignRole}
                        className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                      >
                        <Pencil size={20} className="text-[#6E7397]" />
                        Edit roles
                      </DropdownMenu.Item>
                      <DropdownMenu.Item
                        onSelect={onAddPermissions}
                        className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                      >
                        <Shield size={20} className="text-[#6E7397]" />
                        Add permissions
                      </DropdownMenu.Item>
                    </>
                  )}
                  <DropdownMenu.Item
                    onSelect={onRemoveMember}
                    className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#D62525] outline-none transition-colors focus:bg-[#FFF1F1]"
                  >
                    <UserMinus size={20} className="text-[#D62525]" />
                    Remove member
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>

        <section>
          <div className="text-[14px] font-[500] text-[#161827]">Contact</div>
          <div className="mt-2 flex items-center justify-between text-[16px] text-[#2C2F44]">
            <span>{activeMember?.phone ?? "No contact added"}</span>
            <Call size={22} className="text-[#3A3E5B]" />
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-[500] text-[#161827]">Roles</div>
            <button
              type="button"
              className="text-[14px] font-[500] text-[#5B21FF]"
              onClick={onAssignRole}
            >
              Edit
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {activeMember?.roles.length ? (
              activeMember.roles.map((role) => (
                <span
                  key={role}
                  className="rounded-[8px] bg-[#EEF1FB] px-3 py-1 text-[14px] text-[#3A3E5B]"
                >
                  {role}
                </span>
              ))
            ) : (
              <span className="text-[14px] text-[#6B6F8A]">
                No assigned role yet
              </span>
            )}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-[500] text-[#161827]">
              Additional permissions
            </div>
            {hiddenPermissionsCount > 0 ? (
              <button
                type="button"
                className="text-[14px] font-[500] text-[#161827]"
                onClick={() => setShowAllPermissions((prev) => !prev)}
              >
                {showAllPermissions ? "Show less" : "Show all"}
              </button>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {visiblePermissions.length ? (
              <>
                {visiblePermissions.map((permission) => (
                  <span
                    key={permission}
                    className="rounded-full bg-[#EEF1FB] px-3 py-1 text-[14px] text-[#2C2F44]"
                  >
                    {formatPermissionLabel(permission)}
                  </span>
                ))}
                {!showAllPermissions && hiddenPermissionsCount > 0 ? (
                  <span className="rounded-full bg-[#EEF1FB] px-3 py-1 text-[14px] text-[#2C2F44]">
                    +{hiddenPermissionsCount} more
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-[14px] text-[#6B6F8A]">
                No extra permissions
              </span>
            )}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between">
            <div className="text-[14px] font-[500] text-[#161827]">
              Managed customers
            </div>
          </div>

          <div className="mt-2 rounded-[16px] border border-[#E4E7F4] bg-white p-3">
            <div className="flex h-11 items-center gap-2 rounded-[12px] bg-[#E9ECFA] px-3">
              <SearchNormal size={18} className="text-[#737AA8]" />
              <input
                value={customerQuery}
                onChange={(event) => setCustomerQuery(event.target.value)}
                placeholder="Search customer"
                className="w-full bg-transparent text-[14px] text-[#2C2F44] placeholder:text-[#737AA8] focus:outline-none"
              />
            </div>

            <div className="mt-2 space-y-1">
              {pagedCustomers.length ? (
                pagedCustomers.map((business) => (
                  <div
                    key={business.id}
                    className="flex items-center justify-between rounded-[10px] px-2 py-2 hover:bg-[#F7F8FD]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="grid h-8 w-8 place-items-center rounded-full bg-[#EEF1FB] text-[14px] font-[600] text-[#6E7397]">
                        {business.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-[14px] font-[500] text-[#2C2F44]">
                        {business.name}
                      </span>
                    </div>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button type="button" className="text-[#6E7397]">
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={8}
                          className="z-[120] min-w-[280px] rounded-[16px] border border-[#E1E6FF] bg-white p-2 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
                        >
                          <DropdownMenu.Item
                            onSelect={() =>
                              onChangeCustomerAccountManager(business.id)
                            }
                            className="flex cursor-pointer items-center gap-3 rounded-[12px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                          >
                            <TagUser size={20} className="text-[#6E7397]" />
                            Change account manager
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                ))
              ) : (
                <div className="rounded-[10px] px-2 py-3 text-[14px] text-[#6B6F8A]">
                  No managed customers found.
                </div>
              )}
            </div>
            {filteredCustomers.length > customersPerPage ? (
              <PaginationControls
                currentPage={customersPage}
                totalPages={totalCustomerPages}
                onPageChange={setCustomersPage}
                className="mt-3 flex items-center gap-2"
              />
            ) : null}
          </div>
        </section>
      </FlyinBody>
    </Flyin>
  );
};

export default MemberDetailsFlyin;
